/**
 * Without Shopify Plus, checkout UI extensions cannot apply gift cards.
 * Rebuild Ajax cart via Storefront API (app proxy) so checkoutUrl already
 * has gift cards applied.
 *
 * Uses capture-phase delegation so the handler survives cart section morphs.
 * preventDefault must run synchronously — after await the form already submits.
 */
(function initCartGiftCheckout() {
  /** @type {boolean} */
  let inFlight = false;

  /**
   * Sync signal that gift cards are on the cart (virtual rows / remove buttons).
   * @returns {boolean}
   */
  function hasGiftCardInDom() {
    return Boolean(document.querySelector('[data-gift-card-code][data-code-type="gift_card"]'));
  }

  function goToNativeCheckout() {
    window.location.assign('/checkout');
  }

  /**
   * @param {MouseEvent} event
   */
  async function onCheckoutClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const checkoutButton = target.closest('#checkout, button.cart__checkout-button[name="checkout"]');
    if (!(checkoutButton instanceof HTMLButtonElement)) return;
    if (checkoutButton.disabled) return;

    // No gift card UI → leave native checkout alone (no preventDefault).
    if (!hasGiftCardInDom()) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (inFlight) return;

    inFlight = true;
    checkoutButton.disabled = true;

    try {
      let cart;
      try {
        cart = await fetch('/cart.js').then((response) => response.json());
      } catch {
        goToNativeCheckout();
        return;
      }

      const giftCardCodes = String(cart.attributes?.gift_card_codes || '')
        .split(',')
        .map((code) => code.trim())
        .filter(Boolean);

      if (giftCardCodes.length === 0) {
        goToNativeCheckout();
        return;
      }

      const discountCodes = Array.isArray(cart.discount_codes)
        ? cart.discount_codes
            .filter((entry) => entry?.applicable !== false)
            .map((entry) => entry.code)
            .filter(Boolean)
        : [];

      const lines = (cart.items || []).map((item) => {
        const attributes = [];
        if (item.properties && typeof item.properties === 'object') {
          for (const [key, value] of Object.entries(item.properties)) {
            if (value == null || value === '') continue;
            attributes.push({ key: String(key), value: String(value) });
          }
        }
        return {
          merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`,
          quantity: item.quantity,
          ...(attributes.length ? { attributes } : {}),
        };
      });

      const attributePairs = [];
      if (cart.attributes && typeof cart.attributes === 'object') {
        for (const [key, value] of Object.entries(cart.attributes)) {
          if (key === 'gift_card_codes' || key === 'gift_card_balances_cents') continue;
          if (value == null || value === '') continue;
          attributePairs.push({ key: String(key), value: String(value) });
        }
      }

      const response = await fetch('/apps/gift-cards/prepare-checkout', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          lines,
          discountCodes,
          giftCardCodes,
          note: cart.note || '',
          attributes: attributePairs,
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (response.ok && payload.checkoutUrl) {
        window.location.assign(payload.checkoutUrl);
        return;
      }

      console.warn('Gift card checkout prepare failed', response.status, payload);
      window.alert(
        payload.message ||
          'Dárkový poukaz se nepodařilo přenést do pokladny. Otevři v Adminu appku Dárkové poukazy a odsouhlas nová oprávnění (Storefront), pak to zkus znovu.',
      );
      goToNativeCheckout();
    } catch (error) {
      console.warn('Gift card checkout prepare error', error);
      goToNativeCheckout();
    } finally {
      inFlight = false;
      checkoutButton.disabled = false;
    }
  }

  document.addEventListener('click', onCheckoutClick, true);
})();
